import socketIOClient from "socket.io-client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Typography,
} from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import DropZone from "../../elements/DropZone";
import HubSpotSelector from "../HubSpotSelector";
import HubSpotSelectorRedesign from "../HubSpotSelector/HubSpotSelectorRedesign";
import SelectRedesign from "../../redesign/elements/Select";
import classes from "./AccountsHubSpotUploader.module.scss";
import LegacySelect, { SelectOptionType } from "../../elements/Select/Select";
import { DomainRegExp } from "../../../constants/regExp";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { domainSampleLink } from "../../../constants/sampleLinks";
import { errorToaster } from "../../../helpers/toasters";
import {
  getCampaignCompanySortValues,
  getCampaignFoundCompaniesInfo,
  getFoundCompaniesInfoFromLists,
  getHubSpotLists,
  setCompanySortValues,
  setFoundCompanyInfo,
  setHubSpotCompanyLists,
} from "../../../redux/reducers/campaignToCreate";
import { validateHubSpotData } from "../../../helpers/validateHubSpotData";

type QueryObject = {
  propertyName: string;
  operator: string;
  values: string[];
};

interface AccountsHubSpotUploaderProps {
  selectedBgAdvertiserId?: string;
  setSelectedBgAdvertiserId?: (id: string) => void;
  isStepInCampaignCreation?: boolean;
  useRedesign: boolean;
  setAreCompaniesLoading: React.Dispatch<React.SetStateAction<boolean>>;
  hubSpotSelectorButtonLabel: string;
  tierValue: string[];
  setTierValue: React.Dispatch<React.SetStateAction<string[]>>;
  areCompaniesLoading: boolean;
  setAreTargetAccountsOnly: React.Dispatch<React.SetStateAction<boolean>>;
  areTargetAccountsOnly: boolean;
}

const HelperLabel = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginTop: "0 !important",
  fontSize: "13px !important",
  color: `${theme.palette.brand.gray500} !important`,
  marginBottom: "16px !important",
}));

const UploadTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginBottom: 12,
  lineHeight: "25px",
  fontSize: 15,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const SelectorWrapper = styled("div")({
  marginTop: 8,
  maxWidth: 449,
  marginBottom: 8,
  width: "100%",
});

const OrDivider = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  margin: "28px 0",
  gap: 12,
  color: theme.palette.brand.gray500,
  fontSize: 13,
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    background: theme.palette.brand.gray200,
  },
}));

const UploaderRoot = styled("div")({
  width: "100%",
});

const LoadingState = styled("div")({
  flex: 1,
  minHeight: 240,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const AccountsHubSpotUploader: React.FC<AccountsHubSpotUploaderProps> = ({
  selectedBgAdvertiserId,
  setSelectedBgAdvertiserId,
  isStepInCampaignCreation,
  setAreCompaniesLoading,
  useRedesign,
  hubSpotSelectorButtonLabel,
  tierValue,
  setTierValue,
  areCompaniesLoading,
  areTargetAccountsOnly,
  setAreTargetAccountsOnly,
}) => {
  const dispatch = useDispatch();
  const [didErrorOccurInUpload, setUploadError] = useState(false);
  const [hubSpotOption, setHubSpotOption] = useState<"hs_lists" | "hs_tiers">("hs_lists");
  const [selectedList, setSelectedList] = useState<number[]>([]);
  const [queryOpts, setQueryOpts] = useState<QueryObject[]>([]);

  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const apiError = useSelector((state: ReduxApplicationState) => state.apiError);
  const hubSpotLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.hubSpotCompanyLists);
  const companySortValues = useSelector((state: ReduxApplicationState) => state.campaignToCreate.companySortValues);
  const isHubSpotDataPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const campaignTemplate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.campaignTemplate);

  const listOptions = useMemo<SelectOptionType[]>(
    () =>
      hubSpotLists?.lists?.map((list) => ({
        label: list.name,
        value: list.listId.toString(),
        key: list.listId,
      })) ?? [],
    [hubSpotLists],
  );

  const tierOptions = useMemo<SelectOptionType[]>(
    () =>
      companySortValues?.tier_values?.map((tier) => ({
        label: tier.label,
        value: tier.value,
      })) ?? [],
    [companySortValues],
  );

  const processValidatedData = useCallback(
    (uploadedDomains: string[], fileName: string) => {
      setAreCompaniesLoading(true);
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);
      socket.emit("getCSVKickfireMatches", uploadedDomains);
      socket.on("retrievedCSVKickfireMatches", (response: any) => {
        if (!response.success) {
          errorToaster(response.error);
        } else {
          dispatch(setFoundCompanyInfo({ ...response.payload, fromFile: fileName }));
        }
        setAreCompaniesLoading(false);
      });
    },
    [dispatch, setAreCompaniesLoading],
  );

  const validateData = useCallback(
    (dataRows: string[]) => validateHubSpotData(dataRows, DomainRegExp, "Target Account"),
    [],
  );

  const readFile = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result as string));
      reader.addEventListener("error", reject);
      reader.readAsText(file);
    });

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadError(false);
      acceptedFiles.forEach(async (file) => {
        try {
          const csv = await readFile(file);
          const rows = csv.split("\n").map((row) => row.replace(/\r/g, ""));
          const { validatedData, errorMessage } = validateData(rows);
          if (validatedData.length > 0) processValidatedData(validatedData, file.name);
          if (errorMessage) errorToaster(errorMessage, 10000);
        } catch (error) {
          setUploadError(true);
          console.error("File upload error:", error);
        }
      });
    },
    [validateData, processValidatedData],
  );

  const handleProfileTierChange = (e: any) => {
    const { value } = e.target;
    setTierValue(value);
    setQueryOpts([{ propertyName: "hs_ideal_customer_profile", operator: "IN", values: value }]);
  };

  const handleAreTargetsAccountOnlyChange = (_: React.ChangeEvent<{}>, checked: boolean) => {
    setAreTargetAccountsOnly(checked);
  };

  const fetchCompaniesByTier = async () => {
    try {
      if (tierValue.length === 0) {
        errorToaster("Select a HubSpot Ideal Customer Profile Tier.", 5000);
        return;
      }
      setAreCompaniesLoading(true);
      const filters = areTargetAccountsOnly
        ? [...queryOpts, { operator: "EQ", propertyName: "hs_is_target_account", values: ["true"] }]
        : queryOpts;
      dispatch(getCampaignFoundCompaniesInfo(filters, setAreCompaniesLoading));
    } catch (error) {
      errorToaster(error, 5000);
    }
  };

  const fetchCompaniesByList = async () => {
    try {
      if (selectedList.length === 0) {
        errorToaster("Select a List to get Companies.", 5000);
        return;
      }
      setAreCompaniesLoading(true);
      dispatch(getFoundCompaniesInfoFromLists(selectedList, setAreCompaniesLoading));
    } catch (error) {
      errorToaster(error, 5000);
    }
  };

  useEffect(() => {
    if (apiError.apiError) errorToaster(apiError.apiError, 5000);
  }, [apiError]);

  useEffect(() => {
    dispatch(setHubSpotCompanyLists(null));
    dispatch(setCompanySortValues(null));
    setSelectedList([]);

    if (!campaignTemplate) {
      dispatch(
        setFoundCompanyInfo({
          uniqueSegments: [],
          primarySegments: [],
          matches: 0,
          fromFile: null,
        }),
      );
    }

    if (!selectedAdvertiser?.hubspot_authed) return;

    dispatch(getHubSpotLists("company_lists"));
    dispatch(getCampaignCompanySortValues());
  }, [dispatch, selectedAdvertiser, campaignTemplate]);

  const SelectComponent = useRedesign ? SelectRedesign : LegacySelect;
  const HubSpotSelectorComponent = useRedesign ? HubSpotSelectorRedesign : HubSpotSelector;
  const selectorClass = useRedesign ? undefined : classes.selector;

  const hubSpotOptionSelect = (
    <SelectComponent
      className={selectorClass}
      label="Select option"
      options={[
        { label: "HubSpot Segments", value: "hs_lists", key: "hs_lists" },
        { label: "HubSpot Target Accounts", value: "hs_tiers", key: "hs_tiers" },
      ]}
      value={hubSpotOption}
      onChange={(e: any) => setHubSpotOption((e.target as HTMLSelectElement).value as "hs_lists" | "hs_tiers")}
    />
  );

  if (isHubSpotDataPending && useRedesign) {
    return (
      <LoadingState>
        <CircularProgress size={32} />
      </LoadingState>
    );
  }

  return (
    <UploaderRoot>
      {!isStepInCampaignCreation && selectedBgAdvertiserId !== undefined && (
        <div style={{ margin: "3rem 0", width: "20rem" }}>
          <FormControl fullWidth>
            <InputLabel id="advertiser-select-label">Select Advertiser</InputLabel>
            <MuiSelect
              labelId="advertiser-select-label"
              label="Select Advertiser"
              value={selectedBgAdvertiserId}
              onChange={(e) => setSelectedBgAdvertiserId!(e.target.value as string)}
            >
              {advertisers.map((advertiser) => (
                <MenuItem key={advertiser.brandgenAdvertiserId} value={advertiser.brandgenAdvertiserId}>
                  {advertiser.name}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </div>
      )}

      {isHubSpotDataPending ? (
        <Typography className={classes.helperLabel}>Please, wait while we get everything in order...</Typography>
      ) : listOptions.length || tierOptions.length ? (
        <div>
          {useRedesign ? <SelectorWrapper>{hubSpotOptionSelect}</SelectorWrapper> : hubSpotOptionSelect}

          {hubSpotOption === "hs_lists" && (
            <HubSpotSelectorComponent
              label="Select HubSpot Segments"
              value={selectedList}
              options={listOptions}
              onButtonClick={fetchCompaniesByList}
              onChange={(e: any) => setSelectedList(e.target.value)}
              buttonLabel={hubSpotSelectorButtonLabel}
              disabled={areCompaniesLoading}
            />
          )}
          {hubSpotOption === "hs_tiers" && (
            <HubSpotSelectorComponent
              label="Select Target Accounts"
              value={tierValue}
              options={tierOptions}
              onButtonClick={fetchCompaniesByTier}
              onChange={handleProfileTierChange}
              buttonLabel={hubSpotSelectorButtonLabel}
              disabled={areCompaniesLoading}
              isChecked={areTargetAccountsOnly}
              checkboxLabel="Get Target Accounts Only"
              onCheckboxChange={handleAreTargetsAccountOnlyChange}
            />
          )}
        </div>
      ) : useRedesign ? (
        <HelperLabel>
          {selectedAdvertiser?.hubspot_authed
            ? `No ${
                hubSpotOption === "hs_lists" ? "HubSpot Segments" : "HubSpot Target Accounts"
              } found linked to this advertiser's HubSpot Account.`
            : "HubSpot is not connected for this advertiser. Connect HubSpot in the advertiser settings to use company lists."}
        </HelperLabel>
      ) : (
        <Typography className={classes.helperLabel}>
          {selectedAdvertiser?.hubspot_authed
            ? `No ${
                hubSpotOption === "hs_lists" ? "HubSpot Segments" : "HubSpot Target Accounts"
              } found linked to this advertiser's HubSpot Account.`
            : "HubSpot is not connected for this advertiser. Connect HubSpot in the advertiser settings to use company lists."}
        </Typography>
      )}

      {!areCompaniesLoading && !isHubSpotDataPending && (
        <>
          {selectedAdvertiser?.hubspot_authed ? <OrDivider>OR</OrDivider> : <div style={{ marginTop: 28 }} />}
          {useRedesign ? (
            <UploadTitle>Upload Target Accounts via CSV</UploadTitle>
          ) : (
            <Typography variant="h2" className={classes.h2Title}>
              Upload Target Accounts via CSV
            </Typography>
          )}
          <DropZone
            explanatoryText="Upload a CSV file with one column of Target Account. Each entry should be on a new line."
            sampleLink={domainSampleLink}
            onDrop={handleFileDrop}
            didErrorOccurInUpload={didErrorOccurInUpload}
          />
        </>
      )}
    </UploaderRoot>
  );
};

export default AccountsHubSpotUploader;
